import { useCallback, useEffect, useMemo, useState } from 'react';

// 프레젠테이션 모드 — 본문(editor.document 블록)을 큰 글씨 전체화면 슬라이드로 렌더(읽기 전용).
// heading_1 / heading_2 를 만나면 새 슬라이드로 분할(노션 "프레젠테이션"과 유사).
// heading 이 하나도 없으면 전체를 한 장의 깔끔한 리딩 뷰로 보여준다.
// ESC / 닫기 버튼으로 종료, 좌우 화살표(키보드 + 버튼)로 슬라이드 이동.

// 블록의 inline content(텍스트 런 배열)를 평문으로. styledText/link 모두 .text 를 가진다.
function inlineText(content) {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((c) => {
        if (typeof c === 'string') return c;
        if (c && typeof c.text === 'string') return c.text;
        // link 등 중첩 content
        if (c && Array.isArray(c.content)) return inlineText(c.content);
        return '';
      })
      .join('');
  }
  return '';
}

// editor.document(블록 배열)를 슬라이드 배열로 분할.
// 각 슬라이드 = { title, blocks: [...] }. heading_1/2 가 슬라이드 경계.
function buildSlides(docBlocks) {
  const blocks = Array.isArray(docBlocks) ? docBlocks : [];
  const slides = [];
  let current = null;

  const pushCurrent = () => {
    if (current && (current.title || current.blocks.length)) slides.push(current);
  };

  blocks.forEach((b) => {
    const isSlideHeading =
      b.type === 'heading' && (b.props?.level === 1 || b.props?.level === 2);
    if (isSlideHeading) {
      pushCurrent();
      current = { title: inlineText(b.content), blocks: [] };
      return;
    }
    if (!current) current = { title: '', blocks: [] };
    current.blocks.push(b);
  });
  pushCurrent();

  // 내용이 전혀 없으면 빈 슬라이드 1장(안내).
  if (slides.length === 0) slides.push({ title: '', blocks: [] });
  return slides;
}

// 한 블록을 읽기 전용으로 렌더(텍스트 위주 — 프레젠테이션은 큰 글씨 가독성 우선).
function PresentBlock({ block }) {
  const text = inlineText(block.content);
  switch (block.type) {
    case 'heading': {
      const lvl = block.props?.level || 3;
      if (lvl === 1) return <h1 className="pv-h1">{text}</h1>;
      if (lvl === 2) return <h2 className="pv-h2">{text}</h2>;
      return <h3 className="pv-h3">{text}</h3>;
    }
    case 'bulletListItem':
      return <li className="pv-li">{text}</li>;
    case 'numberedListItem':
      return <li className="pv-li pv-li-num">{text}</li>;
    case 'checkListItem':
      return (
        <li className="pv-li pv-li-check">
          <span className="pv-check">{block.props?.checked ? '☑' : '☐'}</span> {text}
        </li>
      );
    case 'quote':
      return <blockquote className="pv-quote">{text}</blockquote>;
    case 'codeBlock':
      return <pre className="pv-code">{text}</pre>;
    case 'callout':
      return <div className="pv-callout">{text}</div>;
    case 'image':
      return block.props?.url ? (
        <img className="pv-img" src={block.props.url} alt={block.props?.caption || ''} />
      ) : null;
    default:
      if (!text) return null;
      return <p className="pv-p">{text}</p>;
  }
}

export default function PresentationView({ blocks, title, onClose }) {
  const slides = useMemo(() => buildSlides(blocks), [blocks]);
  const [idx, setIdx] = useState(0);
  const total = slides.length;

  const go = useCallback(
    (delta) => {
      setIdx((i) => Math.min(total - 1, Math.max(0, i + delta)));
    },
    [total]
  );

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        go(-1);
      }
    }
    window.addEventListener('keydown', onKey);
    // 오버레이 동안 배경 스크롤 잠금
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [go, onClose]);

  const slide = slides[idx] || { title: '', blocks: [] };
  const empty = !slide.title && slide.blocks.length === 0;

  return (
    <div className="pv-overlay" role="dialog" aria-modal="true" aria-label="프레젠테이션 모드">
      <div className="pv-bar">
        <span className="pv-bar-title">{title || '제목 없음'}</span>
        <span className="pv-bar-count">{idx + 1} / {total}</span>
        <button type="button" className="pv-close" onClick={onClose} aria-label="프레젠테이션 닫기">
          ✕ 닫기 (ESC)
        </button>
      </div>

      <div className="pv-stage">
        <button
          type="button"
          className="pv-nav pv-nav-prev"
          onClick={() => go(-1)}
          disabled={idx === 0}
          aria-label="이전 슬라이드"
        >
          ‹
        </button>

        <div className="pv-slide" key={idx}>
          {slide.title ? <h1 className="pv-slide-title">{slide.title}</h1> : null}
          {empty ? (
            <div className="pv-empty">표시할 내용이 없습니다.</div>
          ) : (
            <div className="pv-slide-body">
              {slide.blocks.map((b) => (
                <PresentBlock key={b.id} block={b} />
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          className="pv-nav pv-nav-next"
          onClick={() => go(1)}
          disabled={idx >= total - 1}
          aria-label="다음 슬라이드"
        >
          ›
        </button>
      </div>

      <div className="pv-dots">
        {slides.map((s, i) => (
          <button
            key={i}
            type="button"
            className={`pv-dot${i === idx ? ' is-active' : ''}`}
            onClick={() => setIdx(i)}
            aria-label={`${i + 1}번 슬라이드로 이동`}
          />
        ))}
      </div>
    </div>
  );
}
